import { Box, Button, Stack } from '@mui/material';
import { PlusIcon } from '@phosphor-icons/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  FlowCaseSensitivity,
  FlowFieldDataType,
  type FlowFilterCondition,
  FlowFilterMatchRule,
  FlowFilterOperation,
} from 'flow-schema';
import { useRef } from 'react';
import * as uuid from 'uuid';

import { colors } from 'common';

import { FilterListBlockFilterConditionCard } from './FilterConditionCard.tsx';
import { useReactFlowViewContext } from '../../../context/context.tsx';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField.ts';

function getDefaultFilterConditionField(): FlowFilterCondition {
  return {
    id: uuid.v4(),
    fieldToFilter: '',
    value: '',
    dataType: FlowFieldDataType.String,
    operation: FlowFilterOperation.Equals,
    caseSensitivity: FlowCaseSensitivity.ExactMatch,
  };
}

export function FilterConditionFields(props: {
  setFilterMatchRule: (filterMatchRule: FlowFilterMatchRule) => void;
}) {
  const { setFilterMatchRule } = props;
  const { value: filterConditions = [], setValue } = useSelectedNodeField<FlowFilterCondition[]>(
    'config.filterConfig.filterConditions'
  );
  const { isEditable } = useReactFlowViewContext();

  const parentRef = useRef<HTMLDivElement>(null);
  /**
   * FIXME - rule of hooks violation
   * Compilation Skipped: Use of incompatible library
   * This API returns functions which cannot be memoized without leading to stale UI. To prevent this, by default React Compiler
   * will skip memoizing this component/hook. However, you may see issues if values from this API are passed to other
   * components/hooks that are memoized.
   */
  // eslint-disable-next-line
  const rowVirtualizer = useVirtualizer({
    count: filterConditions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 200,
    overscan: 5,
  });

  return (
    <Stack gap={2}>
      {filterConditions.length > 0 && (
        <Box
          ref={parentRef}
          sx={{
            height: '400px',
            overflowY: 'auto',
            padding: 2,
            border: `1px solid ${colors.extended.gray[85]}`,
            borderRadius: 1,
            scrollbarWidth: 'thin',
            scrollbarColor: `${colors.extended.gray[55]} ${colors.extended.gray[97]}`,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: colors.extended.gray[97],
              borderRadius: 1,
            },
            '&::-webkit-scrollbar-thumb': {
              background: colors.extended.gray[55],
              borderRadius: 1,
              '&:hover': {
                background: colors.extended.gray[65],
              },
            },
          }}
        >
          <Stack
            sx={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
              paddingRight: 1,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const filterCondition = filterConditions[virtualRow.index];
              return (
                <Stack
                  key={filterCondition.id}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    paddingBottom: 1,
                  }}
                >
                  <FilterListBlockFilterConditionCard
                    fieldNamePrefix={`config.filterConfig.filterConditions.${virtualRow.index}`}
                    onDelete={() => {
                      setValue(filterConditions.filter((value) => value.id !== filterCondition.id));
                    }}
                    filterCondition={filterCondition}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}
      <Button
        variant="contained"
        onClick={() => {
          if (filterConditions.length === 0) {
            setFilterMatchRule(FlowFilterMatchRule.All);
          }
          const newConditions = [...filterConditions, getDefaultFilterConditionField()];
          setValue(newConditions);
          requestAnimationFrame(() => {
            rowVirtualizer.scrollToIndex(newConditions.length - 1, { align: 'end' });
          });
        }}
        sx={{ alignSelf: 'flex-start', gap: 1 }}
        disabled={!isEditable}
      >
        <PlusIcon size={12} />
        Add condition
      </Button>
    </Stack>
  );
}
